import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Layout } from '../components/layout';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { X } from 'lucide-react';
import { SEO } from '../components/SEO';
import axios from 'axios';
import { logger } from '../utils/logger';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Fallback data in case API fails
const FALLBACK_FOUNDER = {
  id: 'nadir-khan',
  name: 'Nadir Khan',
  role: 'Founder & Principal',
  photo_url: "https://customer-assets.emergentagent.com/job_a5cf4f6f-f1fe-4fb9-b168-9949a7ee52a2/artifacts/23804lx7_ChatGPT%20Image%20Mar%208%2C%202026%2C%2010_50_28%20PM.png",
  summary: "Over two decades of cross-industry transformation experience. Co-author of The Goldilocks Zone. Named one of 10 Australian speakers to keep an eye on in 2025 by Australian Business Journal.",
  full_bio: "Nadir Khan founded Agilitie on a simple belief: the most impactful advisory work is the kind that makes itself redundant. With a Master's in Software Engineering and over two decades of cross-industry experience spanning financial services, telecommunications, energy, retail, and government, Nadir has led transformation programmes across some of Australia's largest and most complex enterprises.\n\nHis work focuses on the intersection of leadership, organisational design, and delivery environments — understanding not just what needs to change, but why organisations resist it. Nadir is the co-author of The Goldilocks Zone, a framework for managing the environment in which change happens rather than change itself.\n\nNamed one of '10 Australian speakers to keep an eye on in 2025' by Australian Business Journal, Nadir is a regular keynote speaker at industry conferences and a guest on podcasts exploring organisational transformation, leadership, and the dynamics of change. He brings a practitioner's perspective grounded in hands-on advisory work with Australia's largest organisations."
};

const FALLBACK_ADVISORY = {
  id: 'craig-kimball',
  name: 'Craig Kimball',
  role: 'Advisory Partner & Co-Author',
  subtitle: 'GAICD',
  photo_url: "https://customer-assets.emergentagent.com/job_87621aa9-388b-4a4a-b140-4ad09ad180a4/artifacts/pq6k09f2_Polish_20260326_171256347.png",
  summary: "Three decades of technology leadership across banking, insurance, logistics, and digital platforms. Board technology advisor at Rest, co-author of The Goldilocks Zone, and LinkedIn Australia's #1 Influencer for Risk & Resilience in 2025.",
  full_bio: "Craig Kimball is a technology executive, board advisor, and GAICD who has spent over thirty years leading large, multi-disciplinary teams through complex digital transformations. His career spans HSBC, SEEK, Toll Group, Australia Post, ME Bank, and Acenda, with a consistent focus on building enterprise foundations that scale across people, cloud, engineering, service management, cyber resilience, and ways of working.\n\nAt Australia Post, Craig built a nationally recognised agile delivery centre, scaling an engineering team from 60 to 300 and delivering daily product technology releases with 99.999% uptime. At Acenda, he led an award-winning cloud transformation that delivered capex avoidance and annual opex savings. He sits on Rest's Board Risk Committee as Technology Advisor. Named LinkedIn Australia's #1 Influencer for Risk & Resilience in 2025, Craig advises boards and executives on technology risk, modernisation, and the practical adoption of emerging technologies. He brings the operator's perspective to Agilitie's advisory work: the hard-won knowledge of what happens when strategy meets reality inside complex organisations."
};

const FALLBACK_CONSULTANTS = [
  {
    id: 'graeme-robb',
    name: 'Graeme Robb',
    role: 'Senior Consultant',
    photo_url: "https://customer-assets.emergentagent.com/job_a5cf4f6f-f1fe-4fb9-b168-9949a7ee52a2/artifacts/sbu6uodo_ChatGPT%20Image%20Mar%208%2C%202026%2C%2010_50_09%20PM.png",
    summary: "Over 20 years of transformation experience across telecommunications, banking, insurance, energy, healthcare, and retail. Bridges the gap between strategy and technical execution.",
    full_bio: "Graeme brings over 20 years of transformation experience with a strong foundation in technology and technical architecture. His ability to bridge the gap between strategy and execution makes him particularly effective in environments where organisational change depends on getting the technical and human elements working together.\n\nHe has delivered across telecommunications, banking, insurance, energy, healthcare, and retail, building high-performing teams and designing practical solutions to complex delivery challenges. Graeme's approach is pragmatic and grounded — he simplifies problems, enables people, and drives measurable outcomes without unnecessary overhead."
  },
  {
    id: 'tien-tran',
    name: 'Tien Tran',
    role: 'Senior Consultant',
    photo_url: "https://customer-assets.emergentagent.com/job_a5cf4f6f-f1fe-4fb9-b168-9949a7ee52a2/artifacts/8c50cb80_ChatGPT%20Image%20Mar%208%2C%202026%2C%2010_49_00%20PM.png",
    summary: "Close to 18 years of experience in delivery management, team coaching, and business analysis. Brings calm and clarity to complex delivery environments.",
    full_bio: "Tien has close to 18 years of experience in delivery management, team coaching, and business analysis, working with large enterprises navigating significant organisational change. He brings a calm, structured approach to complex delivery environments, helping teams find clarity and build momentum.\n\nBefore consulting, Tien was a music teacher — an experience that gave him a deep appreciation for patience, communication, and drawing the best out of people. That same instinct shapes his work today: meeting teams where they are and helping them perform at their best. Outside of work, Tien competes in Ironman events, which tells you everything you need to know about his commitment and resilience."
  },
  {
    id: 'james-simonetta',
    name: 'James Simonetta',
    role: 'Senior Consultant',
    photo_url: "https://customer-assets.emergentagent.com/job_a5cf4f6f-f1fe-4fb9-b168-9949a7ee52a2/artifacts/u4w8pnx1_ChatGPT%20Image%20Mar%208%2C%202026%2C%2010_50_19%20PM.png",
    summary: "Over 20 years specialising in delivery leadership and transformation. Known for simplifying complexity and building trust in high-pressure environments.",
    full_bio: "James has over 20 years of experience specialising in delivery leadership and transformation. His strength is in bringing clarity and direction to complex delivery environments — aligning teams around clear objectives, improving planning and prioritisation, and ensuring delivery stays connected to strategic intent.\n\nHe is known for a calm, methodical style that builds trust quickly, even in high-pressure settings. James is particularly effective at simplifying complexity, facilitating alignment across teams, and embedding practical ways of working that sustain beyond the engagement. His focus is always on outcomes, not activity."
  },
  {
    id: 'daniel-yong',
    name: 'Daniel Yong',
    role: 'Senior Consultant',
    photo_url: "https://customer-assets.emergentagent.com/job_87621aa9-388b-4a4a-b140-4ad09ad180a4/artifacts/sohq57mk_file_00000000dcbc71fabee948c7f0209373.png",
    summary: "Over 20 years bridging business strategy and technology delivery across financial services, fintech, energy, and digital platforms. Brings rigour and clarity to complex stakeholder environments.",
    full_bio: "Daniel has over 20 years of experience in business analysis, product management, and delivery leadership, working with large enterprises navigating complex digital transformation. He is particularly effective at translating business strategy into clear requirements and delivery plans that engineering teams can act on.\n\nHe has delivered across financial services, fintech, energy, logistics, and SaaS, including work with organisations such as Australia Post, EnergyAustralia, MYOB, and NAB. Daniel's strengths lie in facilitating stakeholder alignment, shaping business cases, and stabilising delivery environments where multiple teams and competing priorities need to be brought into focus. His approach is methodical and collaborative, grounded in getting the right people aligned around the right problems."
  }
];

// Bio Modal Component
const BioModal = ({ person, onClose }) => {
  // Handle Escape key to close modal
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  if (!person) return null;

  return (
    <div 
      className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="bio-modal-overlay"
    >
      <div 
        className="bg-white max-w-3xl w-full relative max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
        data-testid={`bio-modal-${person.id}`}
      >
        {/* Close Button - Sticky at top right */}
        <button
          onClick={onClose}
          className="sticky top-4 float-right mr-4 mt-4 bg-white/90 rounded-full p-1 text-[#4a4744] hover:text-[#1a1a1a] hover:bg-white transition-colors z-20 shadow-sm"
          data-testid="close-bio-modal"
          aria-label="Close"
        >
          <X size={24} />
        </button>

        <div className="grid grid-cols-1 md:grid-cols-2">
          {/* Photo - Full Color */}
          <div className="aspect-square md:aspect-auto overflow-hidden">
            <img 
              src={person.photo_url || person.photo} 
              alt={person.name}
              className="w-full h-full object-cover"
              style={{ objectPosition: 'center 20%' }}
              data-testid={`modal-photo-${person.id}`}
            />
          </div>

          {/* Content */}
          <div className="p-8 md:p-10">
            <p className="text-[#d4782c] font-dm-sans font-semibold text-sm uppercase tracking-wider mb-2">
              {person.role}
            </p>
            <h2 className="font-playfair text-2xl md:text-3xl font-bold text-[#1a1a1a] mb-6">
              {person.name}
            </h2>
            <div className="text-[#4a4744] leading-relaxed space-y-4">
              {(person.full_bio || person.fullBio).split('\n\n').map((paragraph, idx) => (
                <p key={`bio-${person.id}-${idx}`}>{paragraph}</p>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const HeroSection = () => {
  return (
    <section 
      className="min-h-[35vh] flex items-center bg-[#faf9f7]"
      data-testid="people-hero"
    >
      <div className="max-w-4xl mx-auto px-6 lg:px-8 py-16 md:py-20">
        <h1 
          className="font-playfair text-4xl sm:text-5xl lg:text-6xl font-bold italic text-[#1a1a1a] mb-6 animate-fade-in-up"
          data-testid="people-hero-title"
        >
          Our People
        </h1>
        <p 
          className="font-dm-sans text-lg md:text-xl text-[#4a4744] leading-relaxed max-w-3xl animate-fade-in-up animate-delay-200"
          data-testid="people-hero-subtitle"
        >
          Deliberately small. Senior practitioners who've led large-scale transformation across Australia's most complex organisations.
        </p>
      </div>
    </section>
  );
};

const IntroSection = () => {
  const [ref, isVisible] = useScrollReveal(0.1);

  return (
    <section className="py-16 bg-white" data-testid="people-intro">
      <div className="max-w-4xl mx-auto px-6 lg:px-8">
        <p 
          ref={ref}
          className={`text-[#4a4744] text-lg leading-relaxed transition-all duration-700 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          Agilitie is a boutique advisory firm that works with a trusted network of highly experienced transformation practitioners. Engagements are led by founder Nadir Khan and supported by specialists with deep experience across technology, delivery, and organisational design.
        </p>
      </div>
    </section>
  );
};

// Founder Card Component (Same dimensions as Advisory Partner)
const FounderCard = ({ founder, onViewBio }) => {
  const [ref, isVisible] = useScrollReveal(0.1);
  const [isHovered, setIsHovered] = useState(false);

  return (
    <section className="py-8 md:py-12 bg-white" data-testid="founder-section">
      <div className="max-w-4xl mx-auto px-6 lg:px-8">
        <div 
          ref={ref}
          className={`relative bg-white border border-[#e5e2dc] overflow-hidden cursor-pointer transition-all duration-500 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          onClick={() => onViewBio(founder)}
          data-testid="founder-card"
        >
          {/* Orange top bar animation */}
          <div 
            className="absolute top-0 left-0 h-[3px] bg-[#d4782c] transition-all duration-300"
            style={{ width: isHovered ? '100%' : '0%' }}
          />

          <div className="grid grid-cols-1 md:grid-cols-5">
            {/* Photo - Takes up 2 columns on desktop */}
            <div className="md:col-span-2 aspect-square md:aspect-auto overflow-hidden">
              <img 
                src={founder.photo_url || founder.photo} 
                alt={founder.name}
                className="w-full h-full object-cover transition-all duration-400"
                style={{ 
                  filter: isHovered ? 'grayscale(0%)' : 'grayscale(100%)',
                  objectPosition: 'center 15%'
                }}
                data-testid="founder-photo"
              />
            </div>

            {/* Content - Takes up 3 columns on desktop */}
            <div className="md:col-span-3 p-6 md:p-8 flex flex-col justify-center">
              <p className="text-[#d4782c] font-dm-sans font-semibold text-xs uppercase tracking-wider mb-2">
                FOUNDER
              </p>
              <h2 className="font-playfair text-xl md:text-2xl font-bold text-[#1a1a1a] mb-1">
                {founder.name}
              </h2>
              <p className="text-[#d4782c] font-dm-sans font-medium text-base mb-4">
                {founder.role}
              </p>
              <p className="text-[#4a4744] text-sm leading-relaxed mb-4">
                {founder.summary}
              </p>
              <span className="inline-flex items-center gap-2 text-[#d4782c] font-dm-sans font-semibold text-sm">
                Read full bio
                <span className="transition-transform duration-200" style={{ transform: isHovered ? 'translateX(4px)' : 'translateX(0)' }}>→</span>
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Advisory Partner Card Component (Medium-sized, between Founder and Consultants)
const AdvisoryCard = ({ advisory, onViewBio }) => {
  const [ref, isVisible] = useScrollReveal(0.1);
  const [isHovered, setIsHovered] = useState(false);

  if (!advisory) return null;

  return (
    <section className="py-8 md:py-12 bg-white" data-testid="advisory-section">
      <div className="max-w-4xl mx-auto px-6 lg:px-8">
        <div 
          ref={ref}
          className={`relative bg-white border border-[#e5e2dc] overflow-hidden cursor-pointer transition-all duration-500 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          onClick={() => onViewBio(advisory)}
          data-testid="advisory-card"
        >
          {/* Orange top bar animation */}
          <div 
            className="absolute top-0 left-0 h-[3px] bg-[#d4782c] transition-all duration-300"
            style={{ width: isHovered ? '100%' : '0%' }}
          />

          <div className="grid grid-cols-1 md:grid-cols-5">
            {/* Photo - Takes up 2 columns on desktop */}
            <div className="md:col-span-2 aspect-square md:aspect-auto overflow-hidden">
              <img 
                src={advisory.photo_url || advisory.photo} 
                alt={advisory.name}
                className="w-full h-full object-cover transition-all duration-400"
                style={{ 
                  filter: isHovered ? 'grayscale(0%)' : 'grayscale(100%)',
                  objectPosition: 'center 15%'
                }}
                data-testid="advisory-photo"
              />
            </div>

            {/* Content - Takes up 3 columns on desktop */}
            <div className="md:col-span-3 p-6 md:p-8 flex flex-col justify-center">
              <p className="text-[#d4782c] font-dm-sans font-semibold text-xs uppercase tracking-wider mb-2">
                ADVISORY PARTNER
              </p>
              <h2 className="font-playfair text-xl md:text-2xl font-bold text-[#1a1a1a] mb-1">
                {advisory.name}
              </h2>
              {advisory.subtitle && (
                <p className="text-[#4a4744] font-dm-sans text-sm mb-1">
                  {advisory.subtitle}
                </p>
              )}
              <p className="text-[#d4782c] font-dm-sans font-medium text-base mb-4">
                {advisory.role}
              </p>
              <p className="text-[#4a4744] text-sm leading-relaxed mb-4">
                {advisory.summary}
              </p>
              <span className="inline-flex items-center gap-2 text-[#d4782c] font-dm-sans font-semibold text-sm">
                Read full bio
                <span className="transition-transform duration-200" style={{ transform: isHovered ? 'translateX(4px)' : 'translateX(0)' }}>→</span>
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Consultant Card Component
const ConsultantCard = ({ consultant, index, onViewBio }) => {
  const [ref, isVisible] = useScrollReveal(0.1);
  const [isHovered, setIsHovered] = useState(false);

  return (
    <div 
      ref={ref}
      className={`relative bg-white border border-[#e5e2dc] overflow-hidden cursor-pointer transition-all duration-500 w-full ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
      }`}
      style={{ transitionDelay: `${index * 100}ms` }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={() => onViewBio(consultant)}
      data-testid={`consultant-card-${consultant.id}`}
    >
      {/* Orange top bar animation */}
      <div 
        className="absolute top-0 left-0 h-[3px] bg-[#d4782c] transition-all duration-300 z-10"
        style={{ width: isHovered ? '100%' : '0%' }}
      />

      {/* Photo */}
      <div className="aspect-square overflow-hidden">
        <img 
          src={consultant.photo_url || consultant.photo} 
          alt={consultant.name}
          className="w-full h-full object-cover transition-all duration-400"
          style={{ 
            filter: isHovered ? 'grayscale(0%)' : 'grayscale(100%)',
            objectPosition: 'center 15%'
          }}
        />
      </div>
      
      {/* Content */}
      <div className="p-6">
        <h3 className="font-playfair text-xl font-bold text-[#1a1a1a] mb-1">
          {consultant.name}
        </h3>
        <p className="text-[#d4782c] font-dm-sans font-medium text-sm mb-4">
          {consultant.role}
        </p>
        <p className="text-[#4a4744] text-sm leading-relaxed mb-4">
          {consultant.summary}
        </p>
        <span className="inline-flex items-center gap-2 text-[#d4782c] font-dm-sans font-semibold text-sm">
          Read full bio
          <span className="transition-transform duration-200" style={{ transform: isHovered ? 'translateX(4px)' : 'translateX(0)' }}>→</span>
        </span>
      </div>
    </div>
  );
};

const ConsultantsSection = ({ consultants, onViewBio }) => {
  return (
    <section className="py-12 md:py-16 bg-white" data-testid="consultants-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {consultants.map((consultant, index) => (
            <ConsultantCard 
              key={consultant.id} 
              consultant={consultant} 
              index={index}
              onViewBio={onViewBio}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

const EthosCallout = () => {
  const [ref, isVisible] = useScrollReveal(0.1);

  return (
    <section className="py-16 md:py-20 bg-[#faf9f7]" data-testid="ethos-callout">
      <div className="max-w-4xl mx-auto px-6 lg:px-8">
        <div 
          ref={ref}
          className={`bg-white border-l-[4px] border-[#d4782c] p-8 md:p-10 transition-all duration-700 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <p className="text-[#4a4744] text-lg leading-relaxed">
            "We don't attach ourselves to clients. We aggressively work toward uplifting internal capability and change momentum from within — to the point that reliance on external advisory becomes redundant. Our goal is to make our clients self-reliant, self-sustaining, and self-sufficient."
          </p>
        </div>
      </div>
    </section>
  );
};

const CTASection = () => {
  const [ref, isVisible] = useScrollReveal(0.1);

  return (
    <section className="py-24 bg-white" data-testid="people-cta-section">
      <div className="max-w-3xl mx-auto px-6 lg:px-8 text-center">
        <div 
          ref={ref}
          className={`transition-all duration-700 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <h2 className="font-playfair text-3xl md:text-4xl font-bold text-[#1a1a1a] mb-4">
            Ready to create the conditions for meaningful change?
          </h2>
          <p className="text-[#4a4744] text-lg mb-10">
            We'd love to understand your challenges and explore how we can help.
          </p>
          <Link 
            to="/contact" 
            className="btn-primary inline-block"
            data-testid="people-cta-button"
          >
            Start a Conversation
          </Link>
        </div>
      </div>
    </section>
  );
};

export default function PeoplePage() {
  const [selectedPerson, setSelectedPerson] = useState(null);
  const [founder, setFounder] = useState(FALLBACK_FOUNDER);
  const [advisory, setAdvisory] = useState(FALLBACK_ADVISORY);
  const [consultants, setConsultants] = useState(FALLBACK_CONSULTANTS);
  const [loading, setLoading] = useState(true);

  // Fetch team members from API
  useEffect(() => {
    const fetchTeam = async () => {
      try {
        const response = await axios.get(`${API}/team`);
        const members = response.data;
        
        const founderData = members.find(m => m.member_type === 'founder');
        const advisoryData = members.find(m => m.member_type === 'advisory');
        const consultantData = members.filter(m => m.member_type === 'consultant');
        
        if (founderData) setFounder(founderData);
        if (advisoryData) setAdvisory(advisoryData);
        if (consultantData.length > 0) setConsultants(consultantData);
      } catch (err) {
        logger.error('Failed to fetch team members, using fallback data:', err);
        // Fallback data is already set as initial state
      } finally {
        setLoading(false);
      }
    };

    fetchTeam();
  }, []);

  const handleViewBio = (person) => {
    setSelectedPerson(person);
    document.body.style.overflow = 'hidden';
  };

  const handleCloseBio = () => {
    setSelectedPerson(null);
    document.body.style.overflow = '';
  };

  return (
    <Layout>
      <SEO 
        title="Our People"
        path="/people"
        description="Meet the Agilitie team — experienced transformation advisors led by founder Nadir Khan, helping Australian organisations create meaningful, lasting change."
      />
      <HeroSection />
      <IntroSection />
      <FounderCard founder={founder} onViewBio={handleViewBio} />
      <AdvisoryCard advisory={advisory} onViewBio={handleViewBio} />
      <ConsultantsSection consultants={consultants} onViewBio={handleViewBio} />
      <EthosCallout />
      <CTASection />
      
      {/* Bio Modal */}
      {selectedPerson && (
        <BioModal person={selectedPerson} onClose={handleCloseBio} />
      )}
    </Layout>
  );
}
